import Button from '@atlaskit/button/new';
import { Code } from '@atlaskit/code';
import { HelperMessage, Label } from '@atlaskit/form';
import { Box, Inline, Stack } from '@atlaskit/primitives';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { useCallback, useState } from 'react';
import type { ChangeEvent } from 'react';

import type { SessionIssue } from '../../utils/issue-json';
import { createLocalStorageProvider } from '../../utils/local-storage';
import { generatePrompt } from '../../utils/prompt';
import { isValidRepositoryUrl } from '../../utils/url';

type FormProps = {
	initialBranchName: string;
	initialRepositoryUrl?: string | null;
	isCreatingFlow?: boolean;
	selectedIssue?: SessionIssue;
	onCancel: () => void;
	onSubmit: (repositoryUrl: string, branchName: string, prompt: string) => void;
};

const validateRepositoryUrl = (url: string) => {
	return isValidRepositoryUrl(url);
};

const validateBranchName = (name: string) => {
	return name?.trim() !== '';
};

export const Form = ({
	initialBranchName,
	initialRepositoryUrl,
	isCreatingFlow,
	selectedIssue,
	onCancel,
	onSubmit,
}: FormProps) => {
	const localStorage = createLocalStorageProvider('boysenberryRepository');

	const [prompt, setPrompt] = useState(() =>
		selectedIssue ? generatePrompt({ issue: selectedIssue }) : '',
	);

	const [repositoryUrl, setRepositoryUrl] = useState(
		() => initialRepositoryUrl || localStorage.getItem('cloneUrl') || '',
	);

	const [branchName, setBranchName] = useState(initialBranchName);

	const [isInvalidRepositoryUrl, setIsInvalidRepositoryUrl] = useState(
		!validateRepositoryUrl(repositoryUrl),
	);
	const [isInvalidBranchName, setIsInvalidBranchName] = useState(!validateBranchName(branchName));

	const handleOnChangeField = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setRepositoryUrl(e.target.value);
		setIsInvalidRepositoryUrl(!validateRepositoryUrl(e.target.value));
	}, []);

	const handleBranchNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setBranchName(e.target.value);
		setIsInvalidBranchName(!validateBranchName(e.target.value));
	}, []);

	const handleOnChangePrompt = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
		setPrompt(e.target.value);
	}, []);

	const handleSubmit = () => {
		if (validateRepositoryUrl(repositoryUrl) && validateBranchName(branchName)) {
			localStorage.setItem('cloneUrl', repositoryUrl);
			onSubmit(repositoryUrl, branchName, prompt);
		}
	};

	return (
		<Stack space="space.300">
			<Stack space="space.100">
				{/* eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
				<Label htmlFor="repository-url-input">What's the repository url used in this flow:</Label>
				<Box>
					<Textfield
						id="repository-url-input"
						testId="repository-url-input"
						value={repositoryUrl}
						onChange={handleOnChangeField}
						// eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message
						placeholder="https://bitbucket.org/atlassian/my-repo"
						autoFocus
						isInvalid={isInvalidRepositoryUrl}
					/>
				</Box>
				<HelperMessage>
					{/* eslint-disable-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
					Example:
					{/* eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
					<Code>https://bitbucket.org/atlassian/my-repo</Code>
				</HelperMessage>
			</Stack>

			<Stack space="space.100">
				{/* eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
				<Label htmlFor="branch-name-input">Branch name:</Label>
				<Textfield
					id="branch-name-input"
					testId="branch-name-input"
					value={branchName}
					defaultValue={initialBranchName}
					onChange={handleBranchNameChange}
					isInvalid={isInvalidBranchName}
				/>
			</Stack>

			<Stack space="space.100">
				{/* eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
				<Label htmlFor="prompt">Tell what you would like this flow to do:</Label>
				<Box>
					<TextArea
						id="prompt"
						testId="prompt"
						rows={7}
						value={prompt}
						onChange={handleOnChangePrompt}
						// eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message
						placeholder="Create a landing page for my new product"
					/>
				</Box>
			</Stack>
			<Inline space="space.100" alignInline="end">
				{/* eslint-disable-next-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
				<Button appearance="subtle" onClick={onCancel}>
					Cancel
				</Button>
				<Button
					appearance="primary"
					testId="confirm-repository"
					onClick={handleSubmit}
					isDisabled={isInvalidRepositoryUrl || isInvalidBranchName}
					isLoading={isCreatingFlow}
					interactionName="boysenberry-create-flow"
				>
					{/* eslint-disable-line @dev-agents/rules/i18n/text-wrapped-in-format-message */}
					Confirm
				</Button>
			</Inline>
		</Stack>
	);
};
